import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';
import axios from 'axios';
import { cn, numberToString } from 'common/utils';
import { getPostUrlToShare } from 'common/utils/new-post';
import { CommentIcon, LikeIcon, ShareIcon } from 'components/v2/atoms/icon';
import { isBoolean } from 'lib/axios';
import useGlobalStore from 'store/global';
import usePostReactionStore from 'store/post_reaction';

import { PostItemContext } from './post-item-context';

const likeCountDiff = (
  isLiked: boolean,
  currentLikedStatus: { isLiked: boolean; expiredAt: number } | null,
) => {
  if (
    !currentLikedStatus ||
    !isBoolean(currentLikedStatus?.isLiked) ||
    currentLikedStatus?.isLiked === isLiked
  ) {
    return 0;
  }
  if (isLiked && !currentLikedStatus?.isLiked) {
    return -1;
  }
  if (!isLiked && currentLikedStatus?.isLiked) {
    return 1;
  }
};

const ReactionBar = () => {
  const { post, screen } = useContext(PostItemContext);

  // Global stores
  const showLogin = useGlobalStore((state) => state.showLogin);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const setPostToShareByPopup = useGlobalStore(
    (state) => state.setPostToShareByPopup,
  );
  const router = useRouter();
  // Post reaction stores
  const likedPostsInfo = usePostReactionStore((state) => state.likedPostsInfo);
  const updateLikeInfoOfPost = usePostReactionStore(
    (state) => state.updateLikeInfoOfPost,
  );
  const sharePostsInfo = usePostReactionStore((state) => state.sharePostsInfo);
  const addShareInfoOfPost = usePostReactionStore(
    (state) => state.addShareInfoOfPost,
  );

  // Local state with diff calculation
  const [isLiked, setIsLiked] = useState(() => {
    const diff = likeCountDiff(post.is_l, likedPostsInfo?.[String(post.id)]);
    return diff === 0
      ? post.is_l
      : !!likedPostsInfo?.[String(post.id)]?.isLiked;
  });

  const [isShared, setIsShared] = useState(post.is_s);

  const [likeCount, setLikeCount] = useState(() => {
    return Math.max(
      post.l_count +
        likeCountDiff(post.is_l, likedPostsInfo?.[String(post.id)]),
      0,
    );
  });

  // Sync state when post data or stores change
  useEffect(() => {
    const diff = likeCountDiff(post.is_l, likedPostsInfo?.[String(post.id)]);
    setIsLiked(
      diff === 0 ? post.is_l : !!likedPostsInfo?.[String(post.id)]?.isLiked,
    );
    setIsShared(sharePostsInfo?.[String(post.id)]?.isShared || post.is_s);
    setLikeCount(Math.max(post.l_count + diff, 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.l_count, post.is_s, post.is_l]);

  const postDetailsUrl = `/${post.user_info.username}/posts/${post.id}`;

  // Tracking function

  // Like handler with API call
  const handleLikeClick = async (postId: number, isLiked: boolean) => {
    if (!currentAuth) {
      showLogin({ redirectUrl: postDetailsUrl });
      return;
    }
    const action = isLiked ? 'unlike' : 'like';
    if (isLiked) {
      setIsLiked(false);
      setLikeCount((count) => count - 1);
      updateLikeInfoOfPost(postId, false);
    } else {
      setIsLiked(true);
      setLikeCount((count) => count + 1);
      updateLikeInfoOfPost(postId, true);
    }
    try {
      await axios.post(`/posts/${postId}/reaction?action=${action}`, {});
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  // Share handler with API call
  const handleShareClick = async (postId: number) => {
    if (!currentAuth) {
      showLogin({ redirectUrl: postDetailsUrl });
      return;
    }
    if (isShared) return;

    try {
      await axios.post(`/posts/${postId}/shares`, {});
      if (!isShared) {
        setIsShared(true);
        addShareInfoOfPost(postId);
      }
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  const handleLike = () => {
    void handleLikeClick(post.id, isLiked);
  };

  const handleComment = () => {
    void router.push(postDetailsUrl);
  };

  const handleShare = () => {
    if (!currentAuth) {
      showLogin({ redirectUrl: postDetailsUrl });
      return;
    }
    void handleShareClick(post.id);
    // Convert PostItemType to Post for compatibility
    setPostToShareByPopup({
      postId: post.id.toString(),
      postUrl: getPostUrlToShare(post),
    });
  };

  const ActionButton = ({
    icon,
    count,
    isActive,
    onClick,
    text,
    activeColor = 'text-gray-900',
  }: {
    icon: React.ReactNode;
    count?: number;
    isActive?: boolean;
    onClick: () => void;
    text: string;
    activeColor?: string;
  }) => (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1 py-2 transition-all duration-200 ${
        isActive ? activeColor : 'text-gray-500'
      } hover:opacity-70`}
    >
      <div className="flex size-4 items-center justify-center">{icon}</div>
      <div className="text-sm font-medium">{text}</div>
      {count !== undefined && count > 0 && (
        <span className="text-xs font-normal">{numberToString(count, 1)}</span>
      )}
    </button>
  );

  return (
    <div className="px-4 pb-3">
      <div className="flex justify-between border-t border-gray-100 pt-2">
        {/* Like button */}
        <ActionButton
          icon={
            <LikeIcon
              className={cn(
                'size-4',
                isLiked ? 'fill-pp-green-primary stroke-pp-green-primary' : '',
              )}
            />
          }
          text="いいね"
          count={likeCount}
          isActive={isLiked}
          onClick={handleLike}
          activeColor="text-gray-900"
        />

        {/* Comment button */}
        <ActionButton
          icon={<CommentIcon className="size-4" />}
          count={post.cmt_count}
          text="コメント"
          onClick={handleComment}
        />

        {/* Share button */}
        <ActionButton
          icon={
            <ShareIcon
              className={cn(
                'size-4',
                isShared ? 'stroke-pp-green-primary' : '',
              )}
            />
          }
          text="シェア"
          count={post.s_count}
          isActive={isShared}
          onClick={handleShare}
        />
      </div>
    </div>
  );
};

export default ReactionBar;
